//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { InvisibleButton } from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import Popover from '@components/molecules/popover/Popover';
import { usePopover } from '@components/molecules/popover/usePopover';
import { useCurrentStateData } from '@hooks/useCurrentStateAndParams';
import { getNavGroup } from '@legacy-components/nav3/navigation-menus/NavMenuItem';
import { TrackingId } from '@models/TrackingId';
import { withTracking } from '@utils/withTracking';

import { NavGroup } from 'app/initialization/routing/stateHelpers';

interface Props {
  activeNavGroup: NavGroup[];
  children: ReactNode;
  content: ReactNode;
  trackingId: TrackingId;
  setIsMenuOpen: (isMenuOpen: boolean) => void;
}

export const NavDropdownMenuItem = ({
  activeNavGroup,
  children,
  content,
  setIsMenuOpen,
  trackingId,
}: Props) => {
  const { stateData } = useCurrentStateData();
  const isActive = activeNavGroup.some(
    (navGroup) => navGroup === getNavGroup(stateData),
  );

  const [connectPopover, { closePopover }] = usePopover();

  return (
    <NavItem isActive={isActive}>
      <div
        onMouseEnter={() => setIsMenuOpen(true)}
        onMouseLeave={() => setIsMenuOpen(false)}
      >
        <Popover
          connectPopover={connectPopover}
          content={
            <NavContent
              onClick={() => {
                closePopover();
                setIsMenuOpen(false);
              }}
            >
              {content}
            </NavContent>
          }
          placement='bottom-start'
          shouldShowArrow={false}
          target={
            <NavLink isActive={isActive} trackingId={trackingId}>
              {children}
              <ChevronIcon name='chevron-down' />
            </NavLink>
          }
        />
      </div>
    </NavItem>
  );
};

const NavLink = withTracking(
  styled(InvisibleButton)<{ isActive: boolean }>(
    ({ isActive, theme }) => css`
      display: flex;
      align-items: center;
      background-color: transparent;
      border-radius: ${theme.borderRadii[2]};
      font-weight: ${theme.fontWeights.semiBold};
      height: fit-content;
      padding: 2px ${theme.spacings[3]};
      text-decoration: none;
      width: max-content;
      && div {
        display: flex;
        align-items: center;
        font-weight: ${theme.fontWeights.semiBold};
        color: ${isActive ? '#4756c9' : '#333333'};
      }
      &&:hover {
        background: #ededed;
        && div {
          color: ${isActive ? '#4756c9' : '#333333'};
        }
      }
    `,
  ),
);

const NavItem = styled.div<{ isActive: boolean }>(
  ({ isActive }) => `
    display: flex;
    align-items: center;
    height: 100%;
    ${isActive && `box-shadow: 0 -4px #4756c9 inset;`}
  `,
);

const ChevronIcon = styled(Icon)(
  ({ theme }) => `
    margin-left: ${theme.spacings[1]};
  `,
);

const NavContent = styled.div(
  ({ theme }) => `
    display: flex;
    position: relative;
    margin: ${theme.spacings[3]};
  `,
);
